import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import UpsellDetailModal from './UpsellDetailModal';

const UpsellContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const UpsellGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const UpsellCard = styled.div`
  background: white;
  border: 2px solid ${props => props.isSelected ? props.theme.colors.accent : props.theme.colors.greyBackground};
  border-radius: 16px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  ${props => props.isSelected && `
    border-color: ${props.theme.colors.accent};
    background: ${props.theme.colors.offWhite};
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  `}
`;

const UpsellImage = styled.div`
  width: 100%;
  height: 200px;
  background: ${props => props.imageUrl ? `url(${props.imageUrl})` : props.theme.colors.greyBackground};
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0,0,0,0.1), rgba(0,0,0,0.2));
    border-radius: 12px;
  }
  
  ${props => !props.imageUrl && `
    background: linear-gradient(135deg, ${props.theme.colors.greyBackground}, ${props.theme.colors.primary}20);
    
    &::after {
      content: '🏰';
      font-size: 3rem;
      opacity: 0.3;
    }
  `}
`;

const UpsellContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const UpsellName = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  line-height: 1.3;
`;

const UpsellDescription = styled.p`
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1.25rem;
`;

const UpsellPrice = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  margin-bottom: 0.75rem;
`;

const UpsellAvailability = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.25rem;
  font-weight: 500;
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
`;

const QuantityButton = styled.button`
  width: 40px;
  height: 40px;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  background: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.offWhite};
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuantityInput = styled.input`
  width: 70px;
  text-align: center;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 0.75rem 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const SelectedBadge = styled.div`
  position: absolute;
  top: -8px;
  right: -8px;
  background: ${props => props.theme.colors.accent};
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 600;
`;

const NoUpsells = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.colors.secondary};
  
  p {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  small {
    font-size: 0.9rem;
    opacity: 0.8;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: ${props => props.theme.colors.secondary};
  font-size: 1.1rem;
  
  &::before {
    content: '⟳';
    margin-right: 0.5rem;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const UpsellStep = ({ 
  upsells = [], 
  selectedUpsells = [], 
  onUpsellSelection,
  onUpsellItemAdd,
  selectedDate,
  locationId,
  pageId,
  loading = false 
}) => {
  const [localSelections, setLocalSelections] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUpsell, setSelectedUpsell] = useState(null);

  // Debug logging
  useEffect(() => {


  }, [upsells, selectedUpsells]);

  // Initialize local selections from props
  useEffect(() => {
    const initialSelections = {};
    selectedUpsells.forEach(upsell => {
      initialSelections[upsell.id] = upsell.quantity || 0;
    });
    setLocalSelections(initialSelections);
  }, [selectedUpsells]);

  const handleQuantityChange = (upsellId, quantity) => {
    const newQuantity = Math.max(0, Math.min(quantity, 10)); // Limit to 0-10
    
    setLocalSelections(prev => ({
      ...prev,
      [upsellId]: newQuantity
    }));

    // Find the upsell details
    const upsell = upsells.find(u => u.id === upsellId);
    if (upsell) {
      onUpsellSelection(upsell, newQuantity);
    }
  };

  const incrementQuantity = (upsellId) => {
    const currentQuantity = localSelections[upsellId] || 0;
    handleQuantityChange(upsellId, currentQuantity + 1);
  };

  const decrementQuantity = (upsellId) => {
    const currentQuantity = localSelections[upsellId] || 0;
    handleQuantityChange(upsellId, currentQuantity - 1);
  };

  const handleQuantityInputChange = (upsellId, value) => {
    const quantity = parseInt(value) || 0;
    handleQuantityChange(upsellId, quantity);
  };

  const handleUpsellClick = (upsell) => {
    setSelectedUpsell(upsell);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUpsell(null);
  };

  const handleUpsellItemAdd = (items) => {

    if (onUpsellItemAdd) {
      onUpsellItemAdd(items);
    }
  };

  if (loading) {
    return (
      <UpsellContainer>
        <LoadingSpinner>Loading additional experiences...</LoadingSpinner>
      </UpsellContainer>
    );
  }

  if (upsells.length === 0) {
    return (
      <UpsellContainer>
        <NoUpsells>
          <p>No additional experiences are available for your selected date and time.</p>
          <small>You can proceed with your booking as planned.</small>
        </NoUpsells>
      </UpsellContainer>
    );
  }

  return (
    <UpsellContainer>

      <UpsellGrid>
        {upsells.map((upsell) => {
          const isSelected = (localSelections[upsell.id] || 0) > 0;
          const selectedQuantity = localSelections[upsell.id] || 0;
          
          return (
            <UpsellCard 
              key={upsell.id} 
              isSelected={isSelected}
              onClick={() => handleUpsellClick(upsell)}
            >
              {isSelected && <SelectedBadge>{selectedQuantity}</SelectedBadge>}
              
              <UpsellImage imageUrl={upsell.imageUrl} />
              
              <UpsellContent>
                <div>
                  <UpsellName>{upsell.name}</UpsellName>
                  
                  {upsell.description && (
                    <UpsellDescription>{upsell.description}</UpsellDescription>
                  )}
                  
                  {upsell.price > 0 && (
                    <UpsellPrice>£{upsell.price.toFixed(2)} each</UpsellPrice>
                  )}
                  
                  {upsell.quantityAvailable && (
                    <UpsellAvailability>
                      {upsell.quantityAvailable} spots available
                    </UpsellAvailability>
                  )}
                </div>

                <div style={{ 
                  marginTop: 'auto', 
                  padding: '0.75rem', 
                  background: isSelected ? '#f0f8ff' : '#f8f9fa', 
                  borderRadius: '8px', 
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  color: '#666',
                  fontWeight: '500'
                }}>
                  {isSelected ? `${selectedQuantity} selected` : 'Click to view options'}
                </div>
              </UpsellContent>
            </UpsellCard>
          );
        })}
      </UpsellGrid>

      <UpsellDetailModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        upsell={selectedUpsell}
        selectedDate={selectedDate}
        locationId={locationId}
        pageId={pageId}
        onAddToBasket={handleUpsellItemAdd}
      />
    </UpsellContainer>
  );
};

export default UpsellStep;